import { useEffect } from 'react';

import { expVal, expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import { isStickyInEditMode, isStickyInViewMode } from './isStickyUtilities.ts';

type UseStickyTableHeaderObserverParams = {
	setIsSticky: React.Dispatch<React.SetStateAction<boolean>>;
	isEditor?: boolean;
	elementToObserve?: HTMLElement | null;
};

export const useStickyTableHeaderObserver = ({
	setIsSticky,
	isEditor,
	elementToObserve,
}: UseStickyTableHeaderObserverParams): void => {
	const isStickyHeaderExperimentEnabled =
		expValEquals(
			'platform_editor_table_sticky_header_improvements',
			'cohort',
			'test_with_overflow',
		) && expVal('platform_editor_table_sticky_header_patch_11', 'isEnabled', false);

	// if the experiment is enabled and this is the editor, we need to observe the table element
	// else, we observe the parent element
	const stickyElementToObserve =
		isEditor && isStickyHeaderExperimentEnabled
			? elementToObserve
			: elementToObserve?.parentElement;

	useEffect(() => {
		if (!fg('confluence_frontend_remove_duplicate_sidebar_logic')) {
			return;
		}

		// Create mutation observer to track class changes
		const stickyTableHeaderObserver = new MutationObserver((mutationsList) => {
			mutationsList.forEach((mutation) => {
				if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
					const target = mutation.target as HTMLElement;
					if ((isEditor && isStickyInEditMode(target)) || isStickyInViewMode(target)) {
						setIsSticky(true);
					} else {
						setIsSticky(false);
					}
				}
			});
		});

		// Start observing class changes
		if (stickyElementToObserve) {
			stickyTableHeaderObserver.observe(stickyElementToObserve, {
				attributes: true,
				attributeFilter: ['class'],
			});
		}

		// Clean up all observers
		return () => {
			stickyTableHeaderObserver.disconnect();
		};
	}, [stickyElementToObserve, isEditor, setIsSticky]);
};
